import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Activity,
  ChevronDown,
  ChevronRight,
  Loader2,
  Square,
} from 'lucide-react'
import SessionsPanel from '../SessionsPanel'
import RepositoryPanel from '../git/RepositoryPanel'
import GitHistoryExplorer from '../git/GitHistoryExplorer'
import { HelpLink } from '../HelpLink'
import {
  getLiveAppSource,
  getRepoBinding,
  listAppSessions,
  type AppSession,
  type LiveAppSource,
  type RepoBinding,
} from '../../lib/api'
import {
  sessionChangedFiles,
  sessionHasUnsavedChanges,
} from '../../lib/sessionHelpers'

type SourceReleaseTab = 'work' | 'review' | 'live' | 'advanced'

interface SourceReleasePanelProps {
  appName: string
  appFolder: string
  canEdit: boolean
  liveStatus: string | null
  liveError: string | null
  lastSuccessfulBuild?: string | null
  actionLoading: string | null
  liveLogs: { build_logs: string; runtime_logs: string } | null
  liveLogsExpanded: boolean
  liveLogsLoading: boolean
  onOpenSession: (terminalUrl: string, chromeUrl: string) => void
  onStopLive: () => void
  onToggleLiveLogs: () => void
  formatRelativeTime: (value: string | null | undefined) => string
  focusWorkRequest?: number
  variant?: 'card' | 'workspace'
}

function shortVersion(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  return value.length > 12 ? value.slice(0, 12) : value
}

function liveSourceVersion(source: LiveAppSource | null): string {
  return shortVersion(source?.savedVersionId ?? null)
}

function liveSourceLabel(source: LiveAppSource | null): string {
  return source?.sourceLabel ?? 'main app version'
}

function formatUserFacingStatus(status: string | null): string {
  switch (status) {
    case 'building':
      return 'Publishing...'
    case 'running':
      return 'Live'
    case 'error':
      return 'Update failed'
    case 'stopped':
      return 'Not live'
    default:
      return 'Not live yet'
  }
}

function liveBadgeClass(status: string | null): string {
  switch (status) {
    case 'building':
      return 'border-blue-500/30 bg-blue-500/10 text-blue-500'
    case 'running':
      return 'border-green-500/30 bg-green-500/10 text-green-500'
    case 'error':
      return 'border-red-500/30 bg-red-500/10 text-red-500'
    default:
      return 'border-border bg-secondary text-muted-foreground'
  }
}

function reviewSummary(binding: RepoBinding | null): string {
  if (!binding) return 'No repository connected'
  if (binding.lastSyncStatus === 'failed' || binding.lastSyncStatus === 'error') {
    return 'Latest sync needs attention'
  }
  if (binding.lastSyncStatus === 'queued' || binding.lastSyncStatus === 'running') {
    return 'Sync in progress'
  }
  return 'Connected for review'
}

export default function SourceReleasePanel({
  appName,
  appFolder,
  canEdit,
  liveStatus,
  liveError,
  lastSuccessfulBuild,
  actionLoading,
  liveLogs,
  liveLogsExpanded,
  liveLogsLoading,
  onOpenSession,
  onStopLive,
  onToggleLiveLogs,
  formatRelativeTime,
  focusWorkRequest = 0,
  variant = 'card',
}: SourceReleasePanelProps) {
  const [activeTab, setActiveTab] = useState<SourceReleaseTab>('work')
  const [sessions, setSessions] = useState<AppSession[]>([])
  const [binding, setBinding] = useState<RepoBinding | null>(null)
  const [liveSource, setLiveSource] = useState<LiveAppSource | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(true)

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true)
    try {
      const [sessionResult, nextBinding, sourceResult] = await Promise.all([
        // Always include archived so SessionsPanel can filter client-side (D-10, D-11, D-12).
        listAppSessions(appName, true).catch(() => ({ sessions: [] })),
        getRepoBinding(appName).catch(() => null),
        getLiveAppSource(appName).catch(() => null),
      ])
      setSessions(sessionResult.sessions)
      setBinding(nextBinding)
      setLiveSource(sourceResult?.liveApp ?? null)
    } finally {
      setSummaryLoading(false)
    }
  }, [appName])

  useEffect(() => {
    void loadSummary()
  }, [loadSummary])

  useEffect(() => {
    if (!focusWorkRequest) return
    setActiveTab('work')
    document.getElementById('source-release-heading')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [focusWorkRequest])

  const activeSessions = useMemo(
    () => sessions.filter((session) => session.status !== 'archived'),
    [sessions],
  )
  const changedSessionCount = useMemo(
    () => activeSessions.filter((session) => sessionChangedFiles(session) > 0 || sessionHasUnsavedChanges(session)).length,
    [activeSessions],
  )
  const changedFileCount = useMemo(
    () => activeSessions.reduce((total, session) => total + sessionChangedFiles(session), 0),
    [activeSessions],
  )
  const currentWorkLabel = summaryLoading
    ? 'Checking work areas...'
    : changedFileCount > 0
      ? `${changedFileCount} changed ${changedFileCount === 1 ? 'file' : 'files'} across ${changedSessionCount || 1} work ${changedSessionCount === 1 ? 'area' : 'areas'}`
      : activeSessions.length > 0
        ? `${activeSessions.length} active work ${activeSessions.length === 1 ? 'area' : 'areas'}`
        : 'No work areas yet'

  const liveLabel = liveStatus === 'running'
    ? `Live from ${liveSourceLabel(liveSource)}`
    : formatUserFacingStatus(liveStatus)

  const tabs: Array<{ id: SourceReleaseTab; label: string }> = [
    { id: 'work', label: 'Work' },
    { id: 'review', label: 'Remote' },
    { id: 'live', label: 'Live App' },
    { id: 'advanced', label: 'Advanced History' },
  ]

  return (
    <section
      className={variant === 'workspace' ? 'h-full overflow-y-auto bg-card p-4' : 'p-6 rounded-xl border border-border bg-card'}
      aria-labelledby="source-release-heading"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="source-release-heading" className="text-lg font-semibold">Source & Release</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Current work, review, and live app version.
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-0 border-y border-border md:grid-cols-3">
        <button
          type="button"
          onClick={() => setActiveTab('work')}
          className="flex min-h-[84px] flex-col items-start justify-center gap-1 border-b border-border py-3 text-left md:border-b-0 md:border-r md:pr-4"
        >
          <span className="text-xs font-medium uppercase text-muted-foreground">Current work</span>
          <span className="text-sm font-medium">{currentWorkLabel}</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('review')}
          className="flex min-h-[84px] flex-col items-start justify-center gap-1 border-b border-border py-3 text-left md:border-b-0 md:border-r md:px-4"
        >
          <span className="text-xs font-medium uppercase text-muted-foreground">Remote</span>
          <span className="text-sm font-medium">{reviewSummary(binding)}</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('live')}
          className="flex min-h-[84px] flex-col items-start justify-center gap-1 py-3 text-left md:pl-4"
        >
          <span className="text-xs font-medium uppercase text-muted-foreground">Live app</span>
          <span className="text-sm font-medium">{liveLabel}</span>
        </button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {activeTab === 'work' && (
          <SessionsPanel
            appName={appName}
            onOpenSession={onOpenSession}
            sessions={sessions}
            onSessionsRefresh={loadSummary}
          />
        )}

        {activeTab === 'review' && (
          <RepositoryPanel appName={appName} mode="embedded" hideHistory onBindingChange={setBinding} />
        )}

        {activeTab === 'live' && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${liveBadgeClass(liveStatus)}`}>
                    {formatUserFacingStatus(liveStatus)}
                  </span>
                  {liveStatus === 'running' && (
                    <span className="text-sm text-muted-foreground">
                      This version is live now.
                    </span>
                  )}
                </div>
                <div className="grid gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">Source</span>
                    <p className="font-medium">{liveSourceLabel(liveSource)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Saved version</span>
                    <p className="font-mono text-xs">{liveSourceVersion(liveSource)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Unsaved changes included</span>
                    <p className="font-medium">
                      {(liveSource?.includedUnsavedChanges ?? liveSource?.includedUnsavedChanges) ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last update</span>
                    <p className="font-medium">
                      {lastSuccessfulBuild ? formatRelativeTime(lastSuccessfulBuild) : formatRelativeTime(liveSource?.madeLiveAt ?? liveSource?.madeLiveAt)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {liveStatus === 'running' && canEdit && (
                  <button
                    type="button"
                    onClick={onStopLive}
                    disabled={!!actionLoading}
                    className="inline-flex items-center gap-2 rounded-lg border border-red-500/30 px-3 py-2 text-sm text-red-500 transition-colors hover:bg-red-500/10 disabled:opacity-50"
                  >
                    {actionLoading === 'production-stop' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                    Stop live version
                  </button>
                )}
              </div>
            </div>

            {liveStatus === 'building' && (
              <div className="flex items-center gap-3 rounded-lg border border-blue-500/20 bg-blue-500/10 p-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                <div>
                  <p className="text-sm font-medium">Publishing...</p>
                  <p className="text-xs text-muted-foreground">This may take a few minutes.</p>
                </div>
              </div>
            )}

            {liveStatus === 'error' && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                <p className="text-sm text-red-500">
                  {liveError || 'This update failed. Open logs for details.'}
                </p>
                <div className="mt-2">
                  <HelpLink section="deployment">View deployment guide</HelpLink>
                </div>
              </div>
            )}

            <div className="border-t border-border pt-4">
              <button
                type="button"
                onClick={onToggleLiveLogs}
                className="flex w-full items-center justify-between"
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Activity className="h-4 w-4" />
                  Build logs
                </span>
                <span className="flex items-center gap-2">
                  {liveLogsLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                  {liveLogsExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </span>
              </button>
              {liveLogsExpanded && (
                <div className="mt-2 space-y-3">
                  {liveLogs?.build_logs && (
                    <div>
                      <p className="mb-1 text-xs text-muted-foreground">Build output</p>
                      <div className="max-h-48 overflow-y-auto rounded-lg bg-[#1a1a1a] p-3 font-mono text-xs">
                        <pre className="whitespace-pre-wrap text-gray-300">{liveLogs.build_logs}</pre>
                      </div>
                    </div>
                  )}
                  {liveLogs?.runtime_logs && (
                    <div>
                      <p className="mb-1 text-xs text-muted-foreground">Runtime output</p>
                      <div className="max-h-48 overflow-y-auto rounded-lg bg-[#1a1a1a] p-3 font-mono text-xs">
                        <pre className="whitespace-pre-wrap text-gray-300">{liveLogs.runtime_logs}</pre>
                      </div>
                    </div>
                  )}
                  {!liveLogs?.build_logs && !liveLogs?.runtime_logs && !liveLogsLoading && (
                    <div className="rounded-lg bg-[#1a1a1a] p-3 font-mono text-xs">
                      <span className="text-gray-500">No build logs available</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'advanced' && (
          <div className="space-y-3">
            <GitHistoryExplorer appName={appName} binding={binding} initialLimit={80} />
          </div>
        )}
      </div>

      <span className="sr-only">
        App workspace folder: {appFolder}. Repository connection: {binding ? binding.repoFullName : 'not connected'}.
      </span>
    </section>
  )
}
